import { KdsDropfile } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { useContext } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { useDirectUpload } from "@/hooks/useDirectUpload";

import { type InferredModalAddDeliveryLabelSchema } from "../ModalAddDeliveryLabel.schema";
import { type ModalAddDeliveryLabelDropzoneProps } from "./ModalAddDeliveryLabelDropzone.types";

export const ModalAddDeliveryLabelDropzone = ({
  isLoading,
  setIsLoading,
}: ModalAddDeliveryLabelDropzoneProps) => {
  const { setValue, setError, clearErrors } =
    useFormContext<InferredModalAddDeliveryLabelSchema>();
  const { t } = useTranslate();

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const handleUpload = async (file: File) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryLabels",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      setValue("fileSignedId", newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");

          setError("fileSignedId", {
            type: "manual",
            message: t("dropfile.errorDropfileType"),
          });
          setValue("fileSignedId", undefined);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Controller
      name="fileSignedId"
      render={({ field, fieldState }) => (
        <KdsDropfile
          errorMessage={
            fieldState.error?.message ? t(fieldState.error.message) : undefined
          }
          hasError={!!fieldState.error}
          isDeletable={!!field.value}
          isReadable={!!field.value}
          isLoading={isLoading}
          maxSize={SIZE_5G}
          onDelete={() => field.onChange(undefined)}
          onUploadError={() => setValue("fileSignedId", undefined)}
          onUploadStart={() => {
            setIsLoading(true);
            clearErrors("fileSignedId");
          }}
          onUpload={handleUpload}
          onUploadSuccess={() => setIsLoading(false)}
          placeholderDescription={t(
            "modals.addDeliveryLabel.dropzonePlaceholderDescription"
          )}
          placeholderTitle={t(
            "modals.addDeliveryLabel.dropzonePlaceholderTitle"
          )}
        />
      )}
    />
  );
};
